import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Button from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import PhoneInput from "../../components/PhoneInput";
import TermsCheckbox from "../../components/TermsCheckbox";
import ShopDetailsFields, { EMPTY_SHOP, shopPayload } from "../../components/business/ShopDetailsFields";
import { dialCodeFor } from "../../lib/locations";
import { apiErrorMessage } from "../../lib/utils";

/**
 * Opens a shop for an account that has none — one made with Google or
 * Apple, or a customer who now wants to take bookings. (Signing up with a
 * password opens the shop on the sign-up form itself.) One form, and the
 * shop is live as soon as it is sent.
 */
export default function BusinessOnboarding() {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/register?role=business_owner" replace />;
  if (user.role === "admin") return <Navigate to="/" replace />;

  return <OnboardingForm user={user} />;
}

function OnboardingForm({ user }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [shop, setShop] = useState({
    ...EMPTY_SHOP,
    business_type: params.get("type") === "freelance" ? "freelance" : "business",
    name: params.get("name") || "",
  });
  const [phone, setPhone] = useState(user.phone || "");
  const [whatsappDiffers, setWhatsappDiffers] = useState(false);
  const [payoutDiffers, setPayoutDiffers] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isFreelance = shop.business_type === "freelance";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isFreelance && shop.tin && shop.tin.length !== 9) {
      setError("TIN must be exactly 9 digits.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/owner/business", {
        ...shopPayload(shop, { whatsappDiffers, payoutDiffers }),
        phone: phone || undefined,
        accept_terms: acceptTerms,
      });
      // A customer who opened a shop is now its owner.
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Chose Pro on the pricing page: finish on the Pro payment page.
      navigate(params.get("plan") === "pro" ? "/business/subscription" : "/business");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not create your business. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-14 sm:px-6">
      <h1 className="font-montserrat text-2xl font-bold text-slate-900">
        {isFreelance ? "Tell us about your services" : "Tell us about your business"}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        One form and you're done — your shop goes live on SHUWA straight away. Add your services and customers can book you.
      </p>
      <Card className="mt-6 p-6">
        <form onSubmit={onSubmit} className="space-y-4">
          <ShopDetailsFields
            shop={shop}
            onChange={setShop}
            phone={phone}
            whatsappDiffers={whatsappDiffers}
            setWhatsappDiffers={setWhatsappDiffers}
            payoutDiffers={payoutDiffers}
            setPayoutDiffers={setPayoutDiffers}
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Phone</label>
            <PhoneInput value={phone} onChange={setPhone} dial={dialCodeFor(shop.country)} aria-label="Phone" required />
          </div>
          <TermsCheckbox checked={acceptTerms} onChange={setAcceptTerms} forBusiness />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={submitting || !acceptTerms}>
            {submitting ? "Opening your shop…" : "Open my shop"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
